/**
 * Savings Goals Tab
 */

import React, { useState } from 'react';
import { Plus, Loader2, Target } from 'lucide-react';
import { useSavingsGoals, useSavingsGoalSummary } from '../../hooks/useBudgetOverview';
import { useRegion } from '../../contexts/RegionContext';
import { GoalCard } from './GoalCard';
import { CreateGoalModal } from './CreateGoalModal';
import type { SavingsGoal } from '../../types/savings-goal.types';

interface SavingsGoalsTabProps {
  onDirtyChange?: (dirty: boolean) => void;
}

export function SavingsGoalsTab({ onDirtyChange }: SavingsGoalsTabProps): React.ReactElement {
  const { data: goals = [], isLoading } = useSavingsGoals();
  const { data: summary } = useSavingsGoalSummary();
  const { formatCurrency } = useRegion();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editGoal, setEditGoal] = useState<SavingsGoal | null>(null);

  const activeGoals = goals.filter((g) => g.status === 'active');

  const handleCloseModal = () => {
    onDirtyChange?.(false);
    setShowCreateModal(false);
    setEditGoal(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" aria-label="Loading savings goals" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Savings Goals</h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Track your progress towards financial targets</p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
          >
            <Plus className="h-4 w-4" /> New Goal
          </button>
        </div>

        {summary && activeGoals.length > 0 && (
          <div className="main-page-grid-3-up mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Target</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summary.totalTargetAmount)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{summary.activeGoalCount} active goals</p>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Saved</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summary.totalSaved)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{summary.averageProgressPercent}% avg progress</p>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Target</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(summary.monthlyTargetTotal)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">This month</p>
            </div>
          </div>
        )}

        {activeGoals.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/40 mb-4">
              <Target className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No savings goals yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
              Set financial targets and track your progress towards them.
            </p>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
            >
              <Plus className="h-4 w-4" /> Create Your First Goal
            </button>
          </div>
        ) : (
          <div className="main-page-grid-2">
            {activeGoals.map((goal) => (
              <GoalCard key={goal.id} goal={goal} onEdit={setEditGoal} />
            ))}
          </div>
        )}
      </div>

      {(showCreateModal || editGoal) && (
        <CreateGoalModal
          goal={editGoal}
          onClose={handleCloseModal}
          onDirtyChange={onDirtyChange}
        />
      )}
    </div>
  );
}
